import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon';
import './Button.scss';
import Spinner from '../Spinner/Spinner';
import { createClassList } from '../../utils/dom/class';

const ButtonIcon = ({ loading, icon }) => {
  if (!loading && icon) {
    return <Icon name={icon} />;
  }
  return null;
};

ButtonIcon.propTypes = {
  loading: PropTypes.bool,
  icon: PropTypes.string,
};

ButtonIcon.defaultProps = {
  icon: undefined,
  loading: false,
};

const Button = ({
  id,
  label,
  type,
  icon,
  disabled,
  loading,
  styleVariant,
  attributes,
  onClick,
  inputRef,
  iconPosition,
  iconOnly,
  customAriaLabel,
  classes,
  ariaCurrent,
}) => {
  let standardClasses = ['axiom-button button'];
  if (iconOnly) {
    standardClasses = [...standardClasses, 'button--icon-only'];
  }
  if (styleVariant !== 'primary') {
    standardClasses = [...standardClasses, `button--${styleVariant}`];
  }
  if (icon) {
    standardClasses = [...standardClasses, 'button--icon'];
  }
  if (label) {
    if (iconPosition && iconPosition === 'right') {
      standardClasses = [...standardClasses, 'button--icon-right'];
    } else {
      standardClasses = [...standardClasses, 'button--icon-left'];
    }
  }
  if (loading) {
    standardClasses = [...standardClasses, 'button--loading'];
  }

  const [currentPosition, setCurrentPosition] = useState({
    x: 0,
    y: 0,
    size: 0,
  });
  const [showRipple, setShowRipple] = useState(false);

  useEffect(() => {
    let timer;
    if (showRipple) {
      timer = setTimeout(() => {
        setShowRipple(false);
      }, 700);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [showRipple]);

  function assignSpinnerMode() {
    if (
      styleVariant === 'primary' ||
      styleVariant === 'reversed-secondary'
    ) {
      return 'light';
    }
    return 'dark';
  }

  const setAnimatePosition = (event) => {
    const currentBtn = event.currentTarget.getBoundingClientRect();
    const size =
      currentBtn.width > currentBtn.height
        ? currentBtn.width
        : currentBtn.height;
    const x =
      event.pageX > 0
        ? event.pageX - currentBtn.left - size / 2
        : (event.pageX + event.pageY) / 2;
    const y =
      event.pageY > 0
        ? event.pageY - currentBtn.top - size / 2
        : (event.pageX + event.pageY) / 2;
    return {
      x,
      y,
      size,
    };
  };

  const handleClick = (event) => {
    onClick(event);
    if (!disabled) {
      const position = setAnimatePosition(event);
      setCurrentPosition(position);
      setShowRipple(true);
    }
  };

  function validateLabel() {
    if (!label.trim()) {
      throw new Error(
        'Empty label not valid, label must contain actual text.'
      );
    }
    return label;
  }

  // @todo: research to see if react treats button types differently under the hood; if so, we should fork render based on button type
  // https://github.com/yannickcr/eslint-plugin-react/issues/1846#issuecomment-401849676
  return (
    <button
      className={createClassList({
        default: standardClasses,
        custom: classes,
        required: [],
      })}
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={disabled || loading}
      onClick={(e) => handleClick(e)}
      data-testid='button'
      id={id}
      ref={inputRef}
      {...attributes}
      aria-current={ariaCurrent || null}
      aria-label={customAriaLabel}
    >
      {showRipple && (
        <span
          data-testid='ripple-effect'
          className='axiom-ripple ripple'
          style={{
            left: `${currentPosition.x}px`,
            top: `${currentPosition.y}px`,
            width: currentPosition.size,
            height: currentPosition.size,
          }}
        />
      )}
      {iconPosition === 'left' && (
        <ButtonIcon loading={loading} icon={icon} />
      )}
      {loading && <Spinner mode={assignSpinnerMode()} />}
      <span
        className={
             `axiom-button--label button--label${iconOnly ? ' screen-reader-only' : ''}`
        }
      >
        {validateLabel()}
      </span>
      {iconPosition === 'right' && (
        <ButtonIcon loading={loading} icon={icon} />
      )}
    </button>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['button', 'submit']),
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  styleVariant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'reversed-primary',
    'reversed-secondary',
    'destructive-primary',
    'destructive-secondary',
    'destructive-tertiary',
  ]),
  attributes: PropTypes.objectOf(PropTypes.any),
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  iconPosition: PropTypes.oneOf(['left', 'right']),
  iconOnly: PropTypes.bool,
  customAriaLabel: PropTypes.string,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  ariaCurrent: PropTypes.bool,
};

Button.defaultProps = {
  id: undefined,
  type: 'button',
  disabled: false,
  loading: false,
  styleVariant: 'primary',
  icon: undefined,
  inputRef: undefined,
  attributes: {},
  iconPosition: 'left',
  iconOnly: false,
  customAriaLabel: undefined,
  classes: [],
  ariaCurrent: undefined,
};

ButtonIcon.propTypes = {
  loading: PropTypes.bool,
  icon: PropTypes.string,
};

ButtonIcon.defaultProps = {
  icon: undefined,
  loading: false,
};

export default Button;
